import { FormFieldCheckBoxGroup } from '@/components/FormFieldCheckBoxGroup';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldRadioGroup } from '@/components/FormFieldRadioGroup';
import FormFieldSearch from '@/components/FormFieldSearch';
import { vendorContactSearch, vendorSearch } from '@/services/Network';
import { VendorContactGetAllParams } from '@/types/vendor.types';
import { Action, AfterAction } from '@/types/workOrder.types';
import {
  WorkOrderVendorActionOptions,
  WorkOrderVendorAfterActionOptions,
  WorkOrderVendorDepartmentOptions,
} from '@/utils/constant';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

const SearchIconsProps = {
  Icon: Search,
  iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
};

export const WorkOrderVendorForm = ({ currentWorkOrderIndex }: { currentWorkOrderIndex: number }) => {
  const { control, watch, setValue, getValues } = useFormContext();
  const initialVendorId = getValues(`workOrders[${currentWorkOrderIndex}].vendorDetails.vendorId`) as string;
  const [vendorId, setVendorId] = useState<string | null>(initialVendorId);

  const name = useMemo(() => {
    return `workOrders[${currentWorkOrderIndex}].vendorDetails`;
  }, [currentWorkOrderIndex]);

  const zipCode = watch(`workOrders[${currentWorkOrderIndex}].zipCode`) as string;

  const action = watch(`${name}.action`) as Action;
  const afterAction = watch(`${name}.afterAction`) as AfterAction[];
  const watchedVendorId = watch(`${name}.vendorId`) as string;

  useEffect(() => {
    if (watchedVendorId && watchedVendorId !== vendorId) {
      setVendorId(watchedVendorId);
    }
  }, [watchedVendorId]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 text-black">
      <div className="md:col-span-12">
        <FormFieldRadioGroup
          label="Department:"
          required
          name={`${name}.department`}
          options={WorkOrderVendorDepartmentOptions}
          direction="horizontal"
          optionsInNextLine
        />
      </div>
      <div className="md:col-span-6 pt-2">
        <FormFieldSearch
          name={`${name}.vendorId`}
          required
          label="Vendor Name"
          placeholder="Search Vendor Name"
          props={SearchIconsProps}
          searchFieldName="name"
          api={(params, options) => {
            const zipForCoverage =
              typeof zipCode === 'string' && zipCode.trim().length > 0
                ? zipCode.trim().split(/[\s-]/)[0]?.trim() || zipCode.trim()
                : undefined;
            const updatedParams = {
              ...params,
              pageSize: 100,
              ...(zipForCoverage && { coverageArea: zipForCoverage }),
            };
            return vendorSearch(updatedParams, options, initialVendorId);
          }}
          onChange={(e: string) => {
            if (vendorId && vendorId === e) return;
            setValue(`${name}.vendorContactId`, null);
            setVendorId(e);
          }}
          onSearchChange={() => {
            setVendorId(null);
            setValue(`${name}.vendorContactId`, null);
          }}
          errorMessage={`No Vendor Available`}
        />
      </div>
      <div className="md:col-span-6 pt-2">
        <FormFieldSearch<VendorContactGetAllParams>
          name={`${name}.vendorContactId`}
          label="Vendor's Contact"
          placeholder="Search Vendor's Contact"
          props={SearchIconsProps}
          disabled={!vendorId}
          searchFieldName="search"
          api={(params, options) => {
            return vendorContactSearch(params, vendorId, options);
          }}
        />
      </div>
      <div className="md:col-span-12 pt-2">
        <FormFieldRadioGroup
          label="Service Action:"
          required
          name={`${name}.action`}
          options={WorkOrderVendorActionOptions}
          direction="horizontal"
          optionsInNextLine
        />
      </div>
      {action === Action.OTHER && (
        <div className="md:col-span-12 pt-2">
          <FormFieldComponent
            label="Other"
            required
            name={`${name}.otherAction`}
            control={control}
            allowSpecialCharacters={false}
            placeholder="Enter Other Service action"
          />
        </div>
      )}
      <div className="md:col-span-12 pt-2">
        <FormFieldCheckBoxGroup
          label="After Service Action:"
          name={`${name}.afterAction`}
          options={WorkOrderVendorAfterActionOptions}
          required
        />
      </div>
      {(afterAction ?? []).includes(AfterAction.OTHER) && (
        <div className="md:col-span-12 pt-2">
          <FormFieldComponent
            label="Other"
            required
            name={`${name}.otherAfterAction`}
            control={control}
            allowSpecialCharacters={false}
            placeholder="Enter Other After Service action"
          />
        </div>
      )}
      <div className="md:col-span-6 pt-2">
        <FormFieldComponent
          control={control}
          name={`${name}.amount`}
          label="Amount"
          required
          type="number"
          placeholder="Enter Amount"
        />
      </div>
      <div className="md:col-span-6 pt-2">
        <FormFieldComponent
          control={control}
          name={`${name}.trackingNumber`}
          label="FedEx/ Mail tracking number"
          type="text"
          placeholder="Enter FedEx/ Mail tracking number"
        />
      </div>
    </div>
  );
};
